import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Check, Settings, X, Bell, AlertTriangle, AlertOctagon, Info, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@components/ui';
import { getNotifications } from '@mocks/bildirimler';

const SEVERITY_CONFIG = {
    critical: { icon: AlertOctagon, color: 'text-error', bg: 'bg-error-50' },
    error: { icon: XCircle, color: 'text-error', bg: 'bg-error-50' },
    warning: { icon: AlertTriangle, color: 'text-warning', bg: 'bg-warning-50' },
    info: { icon: Info, color: 'text-info', bg: 'bg-info-50' },
    success: { icon: CheckCircle, color: 'text-success', bg: 'bg-success-50' },
};

const getSeverityConfig = (severity) => SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.info;

const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} dk önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    return `${diffDays} gün önce`;
};

export default function NotificationDropdown({ onClose, onMarkAllRead }) {
    const navigate = useNavigate();
    const { role } = useSelector((state) => state.auth);
    const notifications = getNotifications(5, role);
    const baseRoute = role === 'admin' ? '/yonetici' : '/santiye';

    const handleNotificationClick = (notification) => {
        if (notification.link) {
            navigate(notification.link);
        }
        onClose();
    };

    return (
        <div className="fixed inset-x-3 top-16 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 bg-bg-secondary rounded-xl shadow-xl border border-border-light z-50 overflow-hidden max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border-light bg-bg-tertiary shrink-0">
                <div className="flex items-center gap-2">
                    <Bell size={18} className="text-primary" />
                    <h3 className="font-semibold text-text-primary">Bildirimler</h3>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={onMarkAllRead}
                        className="p-2 text-text-muted hover:text-success hover:bg-success-50 rounded-lg transition-all min-w-[40px] min-h-[40px] flex items-center justify-center"
                        title="Tümünü okundu işaretle"
                    >
                        <Check size={18} />
                    </button>
                    <button
                        onClick={() => { onClose(); navigate(`${baseRoute}/ayarlar`); }}
                        className="p-2 text-text-muted hover:text-primary hover:bg-primary-50 rounded-lg transition-all min-w-[40px] min-h-[40px] flex items-center justify-center"
                        title="Bildirim Ayarları"
                    >
                        <Settings size={18} />
                    </button>
                    <button
                        onClick={onClose}
                        className="p-2 text-text-muted hover:text-error hover:bg-error-50 rounded-lg transition-all min-w-[40px] min-h-[40px] flex items-center justify-center"
                    >
                        <X size={18} />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
                {notifications.length > 0 ? (
                    notifications.map((item) => {
                        const { icon: Icon, color, bg } = getSeverityConfig(item.severity);
                        return (
                            <div
                                key={item.id}
                                className={`px-4 py-3 hover:bg-bg-tertiary transition-colors cursor-pointer border-b border-border-light last:border-0 ${!item.isRead ? 'bg-primary-50/30' : ''}`}
                                onClick={() => handleNotificationClick(item)}
                            >
                                <div className="flex gap-3 items-start">
                                    <div className={`p-2 rounded-lg shrink-0 ${bg}`}>
                                        <Icon size={16} className={color} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start gap-2">
                                            <p className={`text-sm ${!item.isRead ? 'font-semibold text-text-primary' : 'text-text-secondary'} line-clamp-1`}>
                                                {item.title}
                                            </p>
                                            <span className="text-xs text-text-muted whitespace-nowrap shrink-0">
                                                {formatTimeAgo(item.createdAt)}
                                            </span>
                                        </div>
                                        <p className="text-xs text-text-tertiary mt-1 line-clamp-2">
                                            {item.message}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })

                ) : (
                    <div className="px-4 py-12 text-center text-text-muted flex flex-col items-center gap-3">
                        <div className="w-16 h-16 bg-bg-tertiary rounded-full flex items-center justify-center">
                            <Bell size={32} className="text-text-muted opacity-50" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-text-secondary">Bildirim yok</p>
                            <p className="text-xs text-text-muted mt-1">Yeni bildirimler burada görünecek</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-border-light bg-bg-tertiary">
                <Button
                    variant="primary"
                    size="sm"
                    fullWidth
                    onClick={() => { onClose(); navigate(`${baseRoute}/bildirimler`); }}
                >
                    Tüm Bildirimleri Gör
                </Button>
            </div>
        </div>
    );
}
